import { PageFlip } from "page-flip";

const MOBILE_BREAKPOINT = 768;
const RESIZE_DEBOUNCE_MS = 150;
const OVERLAY_TRANSITION_MS = 400;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

export function initFlipBooks() {
    document.querySelectorAll("[data-flip-book]").forEach(initFlipBook);
}

async function initFlipBook(block) {
    const $ = (s) => block.querySelector(s);
    const viewerWrapperEl = $(".pul-flip-book__viewer-wrapper");
    const viewerEl = $(".pul-flip-book__viewer");
    const overlayEl = $(".pul-flip-book__overlay");
    const overlaySlotEl = $(".pul-flip-book__overlay-slot");
    const overlayCloseBtn = $(".pul-flip-book__overlay-close");
    const stageEl = $(".pul-flip-book__stage");
    const bookEl = $(".pul-flip-book__book");
    const prevBtn = $(".pul-flip-book__prev");
    const nextBtn = $(".pul-flip-book__next");
    const fullscreenBtn = $(".pul-flip-book__fullscreen");
    const spreadEl = $(".pul-flip-book__spread");
    const progressEl = $(".pul-flip-book__progress");

    if (!viewerWrapperEl || !viewerEl || !overlayEl || !overlaySlotEl || !stageEl || !bookEl) return;

    let pageFlip = null,
        isMobile = false,
        isOverlayOpen = false,
        isOverlayAnimating = false,
        isReinitializing = false,
        resizeTimer = null,
        lastStageSize = { width: 0, height: 0 };

    const getActiveFlip = () => pageFlip;
    const getFlipSettings = () => ({ showCover: !isMobile, forcePortrait: isMobile });
    const waitForNextFrame = () => new Promise(requestAnimationFrame);

    function resetOverlayState() {
        if (viewerEl.parentElement !== viewerWrapperEl) viewerWrapperEl.appendChild(viewerEl);
        overlayEl.classList.remove("is-open", "is-active");
        overlayEl.setAttribute("aria-hidden", "true");
        block.classList.remove("pul-flip-book--overlay-open", "pul-flip-book--overlay-preparing");
        document.body.classList.remove("pul-flip-book-fullscreen-open");
        updateFullscreenState(fullscreenBtn, false);
        isOverlayOpen = isOverlayAnimating = false;
    }

    resetOverlayState();

    const pageDimensions = await getPageDimensions(block, getPages(bookEl));
    const mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    isMobile = mobileMediaQuery.matches;

    block.style.setProperty("--flip-book-page-aspect-ratio", `${pageDimensions.width} / ${pageDimensions.height}`);

    const updateUi = () => {
        const activeFlip = getActiveFlip();
        if (!activeFlip) return;
        updateSpreadLabel(activeFlip, spreadEl, getFlipSettings());
        if (!progressEl) return;
        progressEl.max = String(activeFlip.getPageCount() - 1);
        progressEl.value = String(activeFlip.getCurrentPageIndex());
        progressEl.setAttribute("aria-valuemax", progressEl.max);
        progressEl.setAttribute("aria-valuenow", progressEl.value);
    };

    const refreshLayout = (force = false) => {
        const activeFlip = getActiveFlip();
        if (!activeFlip || isReinitializing || (isOverlayAnimating && !force)) return;
        const width = stageEl.clientWidth, height = stageEl.clientHeight;
        if (
            !force &&
            Math.abs(width - lastStageSize.width) <= 2 &&
            Math.abs(height - lastStageSize.height) <= 2
        ) return;
        lastStageSize = { width, height };
        activeFlip.update();
        updateUi();
    };

    const scheduleLayoutRefresh = (force = false) => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => refreshLayout(force), RESIZE_DEBOUNCE_MS);
    };

    const mountPageFlip = ({ showCover, forcePortrait }) => {
        const pages = getPages(bookEl);
        if (!pages.length) return null;

        const instance = new PageFlip(bookEl, {
            width: pageDimensions.width,
            height: pageDimensions.height,
            size: "stretch",
            ...getStretchBounds(stageEl, { forcePortrait, inOverlay: isOverlayOpen }),
            maxShadowOpacity: 0,
            showCover,
            mobileScrollSupport: false,
            drawShadow: false,
            showPageCorners: true,
            flippingTime: 800,
            usePortrait: true,
        });

        instance.loadFromHTML(pages);
        instance.on("flip", updateUi);
        instance.on("changeOrientation", updateUi);
        return instance;
    };

    const remountPageFlip = (currentPage = 0) => {
        isReinitializing = true;
        lastStageSize = { width: 0, height: 0 };
        if (pageFlip) teardownPageFlip(pageFlip, bookEl);
        pageFlip = mountPageFlip(getFlipSettings());
        pageFlip?.turnToPage(currentPage);
        isReinitializing = false;
        refreshLayout(true);
        updateUi();
    };

    const applyViewportMode = (mobile, currentPage = 0) => {
        isMobile = mobile;
        block.classList.toggle("pul-flip-book--mobile", mobile);
        remountPageFlip(currentPage);
    };

    function waitForOverlayFade() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return Promise.resolve();
        return new Promise((resolve) => {
            let resolved = false, pending = 2;
            const finish = () => {
                if (resolved) return;
                resolved = true;
                overlayEl.removeEventListener("transitionend", onOverlayTransitionEnd);
                viewerEl.removeEventListener("transitionend", onViewerTransitionEnd);
                resolve();
            };
            const finishOne = () => --pending <= 0 && finish();
            const onOverlayTransitionEnd = (e) => e.target === overlayEl && e.propertyName === "opacity" && finishOne();
            const onViewerTransitionEnd = (e) => e.target === viewerEl && e.propertyName === "opacity" && finishOne();
            overlayEl.addEventListener("transitionend", onOverlayTransitionEnd);
            viewerEl.addEventListener("transitionend", onViewerTransitionEnd);
            setTimeout(finish, OVERLAY_TRANSITION_MS + 50);
        });
    }

    async function openOverlay() {
        if (isOverlayAnimating || isOverlayOpen) return;
        isOverlayAnimating = true;
        const currentPage = getActiveFlip()?.getCurrentPageIndex() ?? 0;
        overlaySlotEl.appendChild(viewerEl);
        overlayEl.classList.add("is-active");
        block.classList.add("pul-flip-book--overlay-preparing");
        overlayEl.setAttribute("aria-hidden", "false");
        document.body.classList.add("pul-flip-book-fullscreen-open");
        await waitForNextFrame();
        await waitForNextFrame();
        isOverlayOpen = true;
        remountPageFlip(currentPage);
        await waitForNextFrame();
        block.classList.remove("pul-flip-book--overlay-preparing");
        block.classList.add("pul-flip-book--overlay-open");
        updateFullscreenState(fullscreenBtn, true);
        overlayEl.classList.add("is-open");
        await waitForOverlayFade();
        isOverlayAnimating = false;
    }

    async function closeOverlay() {
        if (isOverlayAnimating || !isOverlayOpen) return;
        isOverlayAnimating = true;
        const currentPage = getActiveFlip()?.getCurrentPageIndex() ?? 0;
        overlayEl.classList.remove("is-open");
        block.classList.add("pul-flip-book--overlay-preparing");
        updateFullscreenState(fullscreenBtn, false);
        await waitForOverlayFade();
        viewerWrapperEl.appendChild(viewerEl);
        isOverlayOpen = false;
        await waitForNextFrame();
        await waitForNextFrame();
        remountPageFlip(currentPage);
        overlayEl.classList.remove("is-active");
        overlayEl.setAttribute("aria-hidden", "true");
        block.classList.remove("pul-flip-book--overlay-open", "pul-flip-book--overlay-preparing");
        document.body.classList.remove("pul-flip-book-fullscreen-open");
        isOverlayAnimating = false;
    }

    const toggleOverlay = () => !isOverlayAnimating && (isOverlayOpen ? closeOverlay() : openOverlay());

    applyViewportMode(isMobile);

    mobileMediaQuery.addEventListener("change", (event) => {
        applyViewportMode(event.matches, getActiveFlip()?.getCurrentPageIndex() ?? 0);
    });

    prevBtn?.addEventListener("click", () => getActiveFlip()?.flipPrev());
    nextBtn?.addEventListener("click", () => getActiveFlip()?.flipNext());

    progressEl?.addEventListener("input", (event) => {
        const activeFlip = getActiveFlip();
        if (!activeFlip) return;
        activeFlip.turnToPage(parseInt(event.target.value, 10));
        updateUi();
    });

    bookEl.addEventListener("keydown", (event) => {
        const activeFlip = getActiveFlip();
        if (!activeFlip) return;
        if (event.key === "ArrowLeft") activeFlip.flipPrev(), event.preventDefault();
        if (event.key === "ArrowRight") activeFlip.flipNext(), event.preventDefault();
        if (event.key === "Escape" && isOverlayOpen && !isOverlayAnimating) closeOverlay();
    });

    fullscreenBtn?.addEventListener("click", toggleOverlay);
    overlayCloseBtn?.addEventListener("click", () => isOverlayOpen && !isOverlayAnimating && closeOverlay());
    overlayEl.addEventListener("click", (event) => event.target === overlayEl && !isOverlayAnimating && closeOverlay());
    document.addEventListener("keydown", (event) => event.key === "Escape" && isOverlayOpen && !isOverlayAnimating && closeOverlay());

    if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => scheduleLayoutRefresh()).observe(stageEl);
    window.addEventListener("resize", () => scheduleLayoutRefresh());
}

function getPages(bookEl) {
    return bookEl.querySelectorAll(".pul-flip-book__page");
}

function teardownPageFlip(pageFlip, bookEl) {
    pageFlip.clear();
    pageFlip.getUI().destroy();
    bookEl.classList.remove("stf__parent");
    bookEl.style.cssText = "";
}

function getStretchBounds(stageEl, { forcePortrait, inOverlay }) {
    const stageWidth = stageEl.clientWidth || window.innerWidth;
    const stageHeight = stageEl.clientHeight || window.innerHeight;

    if (forcePortrait) {
        const minWidth = Math.ceil(stageWidth / 2) + 1;
        return {
            minWidth,
            maxWidth: Math.max(minWidth, stageWidth),
            minHeight: 200,
            maxHeight: inOverlay
                ? Math.max(200, Math.floor(stageHeight))
                : Math.min(Math.max(200, Math.floor(stageHeight)), 900),
        };
    }

    const pageWidthInset = inOverlay ? 0 : 8;
    const maxPageWidth = Math.max(160, Math.floor(stageWidth / 2) - pageWidthInset);
    const maxPageHeight = Math.max(200, Math.floor(stageHeight));

    return {
        minWidth: 160,
        maxWidth: inOverlay ? maxPageWidth : Math.min(maxPageWidth, 900),
        minHeight: 200,
        maxHeight: inOverlay ? maxPageHeight : Math.min(maxPageHeight, 900),
    };
}

function updateFullscreenState(fullscreenBtn, isOpen) {
    if (!fullscreenBtn) return;
    fullscreenBtn.setAttribute("aria-pressed", String(isOpen));
    fullscreenBtn.setAttribute("aria-label", isOpen ? "Vollbild beenden" : "Vollbild");
}

async function getPageDimensions(block, pages) {
    const dataWidth = parseInt(block.dataset.pageWidth, 10);
    const dataHeight = parseInt(block.dataset.pageHeight, 10);
    if (dataWidth > 0 && dataHeight > 0) return { width: dataWidth, height: dataHeight };

    const firstImage = pages[0]?.querySelector("img");
    if (!firstImage) return { width: 400, height: 600 };

    const attributeWidth = parseInt(firstImage.getAttribute("width"), 10);
    const attributeHeight = parseInt(firstImage.getAttribute("height"), 10);
    if (attributeWidth > 0 && attributeHeight > 0) return { width: attributeWidth, height: attributeHeight };

    if (firstImage.complete && firstImage.naturalWidth > 0) {
        return { width: firstImage.naturalWidth, height: firstImage.naturalHeight };
    }

    await new Promise((resolve, reject) => {
        firstImage.addEventListener("load", resolve, { once: true });
        firstImage.addEventListener("error", reject, { once: true });
    });

    return { width: firstImage.naturalWidth, height: firstImage.naturalHeight };
}

function updateSpreadLabel(pageFlip, spreadEl, { showCover, forcePortrait }) {
    if (!spreadEl) return;
    const current = pageFlip.getCurrentPageIndex();
    const total = pageFlip.getPageCount();
    const isPortrait = pageFlip.getOrientation() === "portrait";

    spreadEl.textContent =
        forcePortrait || isPortrait || (showCover && (current === 0 || current === total - 1))
            ? `${current + 1} / ${total}`
            : `${current + 1}-${Math.min(current + 2, total)} / ${total}`;
}